import { FOTO_DO_PERFIL_TROCADA } from "../casca/AreaDoUsuario"
import { useCallback, useEffect, useState, type ReactNode } from "react"

import { Preferencias } from "./Preferencias"
import {
  api,
  type MudancaDoCadastro,
  type AtoNoPerfil,
  type DesignacaoNoPerfil,
  type EntradaNoPerfil,
  type Opcao,
  type Perfil as PerfilDaConta,
  type RespostaDasEntradas,
} from "../api"
import { Abas, PainelDaAba, type DescricaoDeAba } from "../componentes/Abas"
import {
  mensagemDeRecusa,
  useImagemProtegida,
  type CampoDoBloco,
  type ValoresDoBloco,
} from "../componentes/BlocoDeCampos"
import { Badge } from "../componentes/Badge"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import * as Icones from "../componentes/Icones"
import { InputError } from "../componentes/InputError"
import { PasswordInput } from "../componentes/PasswordInput"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { SecaoDeCampos } from "../componentes/SecaoDeCampos"
import { CarregandoNaSecao, FalhaNaSecao } from "../componentes/Secao"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Skeleton } from "../componentes/Skeleton"
import { Column, TablePrincipal2 } from "../componentes/TablePrincipal2"
import { toast } from "../componentes/Toast"
import { data, dataHora, inteiro, numeroComAno } from "../formato"
import { cn } from "../lib/cn"
import { posicaoGuardada, type PosicaoDaBarra } from "../preferencias"
import {
  ABAS_NO_CARTAO,
  BOTAO_BRANCO,
  CABECALHO_DO_SHEET,
  CARTAO_DAS_ABAS,
  DESTAQUE,
  FAZER,
  LADO_A_LADO,
  MAIUSCULA_INLINE,
  SEGUNDO_LADO,
} from "../lib/classes"
import { comListasDoVinculo, opcoesDosCargos, opcoesDosSetores, pedidoDoVinculo, valoresComIds } from "../lib/vinculo"

type Props = {
  aoAbrirContrato: (id: number) => void
  aoTrocarSenha: (credencial: string, perfil: PerfilDaConta) => void
  aoMudarDados?: (perfil: PerfilDaConta) => void
  aba?: AbaDoPerfil
  aoTrocarAba?: (aba: AbaDoPerfil) => void
  posicao?: PosicaoDaBarra
  aoTrocarPosicao?: (posicao: PosicaoDaBarra) => void
}

export const ABAS_DO_PERFIL = ["dados", "designacoes", "seguranca", "atividade", "preferencias"] as const
export type AbaDoPerfil = (typeof ABAS_DO_PERFIL)[number]

export function ehAbaDoPerfil(valor: string): valor is AbaDoPerfil {
  return (ABAS_DO_PERFIL as readonly string[]).includes(valor)
}

const LIMITE_DE_ENTRADAS = 5
const ATOS_NO_PERFIL = 200
const TAMANHO_DO_RESUMO = 60
const NAO_INFORMADO = "Não informado"
const RECUSA = "m-0 rounded-md border border-destructive bg-muted px-3 py-2.5 text-(length:--texto-md) first-letter:uppercase"
const CONTA = "ml-auto text-(length:--texto-sm) tabular-nums text-muted-foreground"
const AJUDA = "m-0 text-(length:--texto-sm) text-(--tinta-3)"

const GRADE_DE_TRES = { leitura: "", formulario: "grid grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3" }

const resumo = (texto: string | null) =>
  texto === null ? "—" : texto.length > TAMANHO_DO_RESUMO ? `${texto.slice(0, TAMANHO_DO_RESUMO).trimEnd()}…` : texto

export function Perfil({
  aoAbrirContrato,
  aoTrocarSenha,
  aoMudarDados = () => undefined,
  aba: abaDeFora,
  aoTrocarAba,
  posicao = posicaoGuardada(),
  aoTrocarPosicao = () => undefined,
}: Props) {
  const [perfil, definirPerfil] = useState<PerfilDaConta | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [abaPropria, definirAbaPropria] = useState<AbaDoPerfil>("dados")
  const aba = abaDeFora ?? abaPropria
  const definirAba = (nova: AbaDoPerfil) => (aoTrocarAba ? aoTrocarAba(nova) : definirAbaPropria(nova))
  const [listas, definirListas] = useState<{ cargos: Opcao[]; setores: Opcao[] } | null>(null)

  const setorAtual = perfil?.setor_id ?? null
  const cargoAtual = perfil?.cargo_id ?? null

  useEffect(() => {
    let vivo = true
    Promise.all([api.cargos(cargoAtual), api.setores(undefined, setorAtual)])
      .then(([cargos, setores]) => {
        if (vivo) definirListas({ cargos: opcoesDosCargos(cargos), setores: opcoesDosSetores(setores) })
      })
      .catch(() => undefined)
    return () => {
      vivo = false
    }
  }, [setorAtual, cargoAtual])

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .perfil(ATOS_NO_PERFIL)
      .then(definirPerfil)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])

  const recarregarEmSilencio = useCallback(() => {
    api
      .perfil(ATOS_NO_PERFIL)
      .then(definirPerfil)
      .catch(() => undefined)
  }, [])

  useEffect(carregar, [carregar])

  if (perfil === null) {
    return (
      <Card>
        <CardContent>
          {falha !== null ? (
            <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
          ) : (
            <div aria-busy="true" className="flex items-start gap-4">
              <Skeleton className="h-40 w-30" />
              <div className="flex flex-1 flex-col gap-3">
                <Skeleton className="h-7 w-1/3" />
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-5 w-2/3" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  const aplicarPerfil = (novo: PerfilDaConta) => {
    definirPerfil((anterior) =>
      anterior === null ? novo : { ...novo, atos: anterior.atos, atos_totais: anterior.atos_totais },
    )
    recarregarEmSilencio()
  }
  const gravar = async (mudanca: MudancaDoCadastro) => {
    const novo = await api.alterarPerfil(pedidoDoVinculo(mudanca))
    aplicarPerfil(novo)
    aoMudarDados(novo)
  }
  const vigentes = perfil.designacoes.filter((d) => d.vigente).length

  const abas: DescricaoDeAba<AbaDoPerfil>[] = [
    { chave: "dados", rotulo: "Meus dados", icone: Icones.Pessoa },
    { chave: "designacoes", rotulo: "Designações", contagem: vigentes, icone: Icones.Fiscalizacao },
    { chave: "seguranca", rotulo: "Segurança", icone: Icones.Chave },
    { chave: "atividade", rotulo: "Atividade", contagem: perfil.atos_totais, icone: Icones.Historico },
    { chave: "preferencias", rotulo: "Preferências", icone: Icones.Configuracao },
  ]

  return (
    <>
      <Identificacao
        perfil={perfil}
        aoGravar={gravar}
        aoTrocarFoto={() => {
          recarregarEmSilencio()
          window.dispatchEvent(new Event(FOTO_DO_PERFIL_TROCADA))
        }}
      />
      <div data-slot="cartao-das-abas" className={CARTAO_DAS_ABAS}>
        <Abas
          nome="perfil"
          classe={ABAS_NO_CARTAO}
          classeDaLista="max-[767px]:flex-nowrap max-[767px]:overflow-x-auto max-[767px]:[&>*]:flex-none"
          rotulo="Seções do perfil"
          abas={abas}
          atual={aba}
          aoTrocar={definirAba}
        />

        <PainelDaAba nome="perfil" chave="dados" ativa={aba === "dados"}>
          <MeusDados perfil={perfil} listas={listas} aoGravar={gravar} />
        </PainelDaAba>

        <PainelDaAba nome="perfil" chave="designacoes" ativa={aba === "designacoes"}>
          {aba === "designacoes" && <Designacoes perfil={perfil} aoAbrirContrato={aoAbrirContrato} />}
        </PainelDaAba>

        <PainelDaAba nome="perfil" chave="seguranca" ativa={aba === "seguranca"}>
          {aba === "seguranca" && (
            <div data-slot="seguranca-lado-a-lado" className={LADO_A_LADO}>
              <div>
                <TrocaDeSenha
                  perfil={perfil}
                  forma="em-duas-linhas"
                  aoTrocar={(credencial, novo) => {
                    aplicarPerfil(novo)
                    aoTrocarSenha(credencial, novo)
                  }}
                />
              </div>
              <div className={SEGUNDO_LADO}>
                <MinhasEntradas />
              </div>
            </div>
          )}
        </PainelDaAba>

        <PainelDaAba nome="perfil" chave="atividade" ativa={aba === "atividade"}>
          {aba === "atividade" && <Atos perfil={perfil} />}
        </PainelDaAba>

        <PainelDaAba nome="perfil" chave="preferencias" ativa={aba === "preferencias"}>
          {aba === "preferencias" && (
            <Preferencias
              posicao={posicao}
              aoTrocarPosicao={aoTrocarPosicao}
              email={perfil.email}
              telefone={perfil.telefone}
              aoIrParaContato={() => {
                const telefone = document.getElementById("perfil-contato-telefone")
                telefone?.scrollIntoView({ block: "center" })
                telefone?.focus()
              }}
            />
          )}
        </PainelDaAba>
      </div>
    </>
  )
}

function MeusDados({
  perfil,
  listas,
  aoGravar,
}: {
  perfil: PerfilDaConta
  listas: { cargos: Opcao[]; setores: Opcao[] } | null
  aoGravar: (mudanca: MudancaDoCadastro) => Promise<void>
}) {
  const valores = perfil as unknown as ValoresDoBloco
  const camposFuncionais: CampoDoBloco[] = [
    { campo: "matricula", rotulo: "Matrícula" },
    { campo: "cargo", rotulo: "Cargo" },
    { campo: "funcao", rotulo: "Função" },
    { campo: "lotacao", rotulo: "Lotação" },
    { campo: "vinculo", rotulo: "Vínculo", tipo: "escolha", opcoes: perfil.vinculos },
    { campo: "ato_de_nomeacao", rotulo: "Ato de nomeação" },
    { campo: "ato_de_nomeacao_data", rotulo: "Data do ato", tipo: "data" },
  ]
  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <SecaoDeCampos
          key={listas === null ? "sem-listas" : "com-listas"}
          titulo="Dados funcionais"
          dica="Pedidos pelo Tribunal de Contas para quem é designado. Sem matrícula e cargo, a designação de gestor ou fiscal não se remete ao Tribunal."
          prefixo="perfil-funcional"
          campos={listas === null ? camposFuncionais : comListasDoVinculo(camposFuncionais, listas.cargos, listas.setores)}
          valores={listas === null ? valores : valoresComIds(perfil)}
          aoGravar={aoGravar}
          grade={GRADE_DE_TRES}
          mensagemDeSucesso="Dados funcionais salvos"
        />
      </div>
    </div>
  )
}

const TIPOS_DA_FOTO = ["image/png", "image/jpeg"]

function FotoDoPerfil({ perfil, aoTrocarFoto }: { perfil: PerfilDaConta; aoTrocarFoto: () => void }) {
  const [previa, definirPrevia] = useState<{ arquivo: File; endereco: string } | null>(null)
  const [enviando, definirEnviando] = useState(false)
  const { endereco } = useImagemProtegida(
    () => (perfil.tem_foto ? api.minhaFoto() : Promise.resolve(null)),
    perfil.foto_enviada_em ?? "sem-foto",
  )
  const limite = perfil.foto_limite_kb ?? null
  const regra = `PNG ou JPEG${limite !== null ? `, até ${inteiro(limite)} KB` : ""}`

  useEffect(
    () => () => {
      if (previa !== null) URL.revokeObjectURL(previa.endereco)
    },
    [previa],
  )

  const escolher = (arquivo: File | undefined) => {
    if (arquivo === undefined) return
    if (!TIPOS_DA_FOTO.includes(arquivo.type)) {
      toast.error(`A foto precisa ser ${regra}.`)
      return
    }
    if (limite !== null && perfil.foto_limite_bloqueia === true && arquivo.size > limite * 1024) {
      toast.error(`A imagem tem ${inteiro(Math.ceil(arquivo.size / 1024))} KB, e o limite é ${inteiro(limite)} KB. Reduza a imagem e escolha de novo.`)
      return
    }
    definirPrevia({ arquivo, endereco: URL.createObjectURL(arquivo) })
  }

  const enviar = async () => {
    if (previa === null) return
    definirEnviando(true)
    try {
      const resposta = await api.enviarMinhaFoto(previa.arquivo)
      if (resposta.alerta !== null) toast.success("Foto enviada", { description: resposta.alerta })
      else toast.success("Foto enviada")
      definirPrevia(null)
      aoTrocarFoto()
    } catch (erro) {
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirEnviando(false)
    }
  }

  const mostrada = previa?.endereco ?? endereco
  return (
    <div data-slot="perfil-foto-coluna" className="flex shrink-0 flex-col items-center gap-3 text-center">
      <div
        data-slot="perfil-foto-moldura"
        className={cn(
          "flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted sm:size-24",
          previa !== null ? "border-primary" : "border-(--borda)",
        )}
      >
        {mostrada !== null ? (
          <img
            className="h-full w-full object-cover"
            src={mostrada}
            alt={previa !== null ? "Prévia da nova foto" : `Foto de ${perfil.nome}`}
          />
        ) : (
          <span className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
            <Icones.Pessoa tamanho={30} />
            Sem foto
          </span>
        )}
      </div>
      <div className="flex flex-col items-center gap-1.5">
        {previa !== null ? (
          <div className="flex flex-col gap-1.5">
            <Button type="button" size="sm" disabled={enviando} onClick={() => void enviar()}>
              <Icones.Enviar />
              {enviando ? "Enviando..." : "Enviar esta foto"}
            </Button>
            <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} disabled={enviando} onClick={() => definirPrevia(null)}>
              <Icones.Fechar />
              Cancelar
            </Button>
          </div>
        ) : (
          <Button asChild variant="outline" size="sm" className={BOTAO_BRANCO}>
            <label className="cursor-pointer focus-within:ring-[3px] focus-within:ring-ring/50">
              <Icones.Enviar />
              {perfil.tem_foto ? "Trocar a foto" : "Enviar foto"}
              <input
                type="file"
                name="minha-foto"
                accept="image/png,image/jpeg"
                className="sr-only"
                onChange={(evento) => {
                  const arquivo = evento.target.files?.[0]
                  evento.target.value = ""
                  escolher(arquivo)
                }}
              />
            </label>
          </Button>
        )}
        <span className="max-w-40 text-xs text-muted-foreground">{regra}</span>
      </div>
    </div>
  )
}

function CampoDoPerfil({ rotulo, valor, Icone }: { rotulo: string; valor: ReactNode; Icone: Icones.Icone }) {
  const vazio = valor === null || valor === undefined || valor === ""
  return (
    <div data-slot="campo-do-perfil" className="flex min-w-0 items-start gap-3">
      <span aria-hidden="true" className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icone tamanho={16} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm text-muted-foreground">{rotulo}</span>
        <span className={cn("text-sm font-medium wrap-anywhere", vazio ? "text-muted-foreground" : "text-foreground")}>
          {vazio ? NAO_INFORMADO : valor}
        </span>
      </div>
    </div>
  )
}

const CAMPO_DO_NOME: CampoDoBloco[] = [{ campo: "nome", rotulo: "Nome", obrigatorio: true, rotuloOculto: true }]
const CAMPOS_DE_CONTATO: CampoDoBloco[] = [
  { campo: "email", rotulo: "E-mail", tipo: "email", obrigatorio: true },
  { campo: "telefone", rotulo: "Telefone", tipo: "telefone" },
]
const UMA_COLUNA = { leitura: "", formulario: "grid grid-cols-1 gap-3" }

function Identificacao({
  perfil,
  aoTrocarFoto,
  aoGravar,
}: {
  perfil: PerfilDaConta
  aoTrocarFoto: () => void
  aoGravar: (mudanca: MudancaDoCadastro) => Promise<void>
}) {
  const perfis = perfil.perfis_na_tela?.length
    ? perfil.perfis_na_tela
    : [{ papel: perfil.papel, nome: perfil.papel_nome, pode: perfil.papel_pode }]
  const variasEntidades = (perfil.entidades?.length ?? 0) > 1
  const valores = perfil as unknown as ValoresDoBloco
  const entidade = variasEntidades ? (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      {perfil.entidades.map((e) => (
        <li key={e.id}>
          {e.sigla}
          {e.id === perfil.entidade_id && <span className="ml-1.5 text-xs font-semibold text-(--marca-forte)">· Nesta sessão</span>}
        </li>
      ))}
    </ul>
  ) : perfil.entidade === null ? (
    "Todas as entidades"
  ) : (
    `${perfil.entidade_nome ?? ""} (${perfil.entidade})`
  )

  return (
    <Card>
      <CardContent>
        <div data-slot="perfil-identificacao" className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)] lg:gap-8">
          <section aria-label="Identificação" className="min-w-0">
            <div className="flex flex-col gap-5 sm:flex-row">
              <FotoDoPerfil perfil={perfil} aoTrocarFoto={aoTrocarFoto} />
              <div data-slot="perfil-quem" className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div data-slot="perfil-nome-e-perfil" className="flex flex-wrap items-start gap-2">
                    <div className="min-w-48 flex-1">
                      <SecaoDeCampos
                        titulo="Nome da conta"
                        tituloVisivel={false}
                        prefixo="perfil-identificacao"
                        campos={CAMPO_DO_NOME}
                        valores={valores}
                        aoGravar={aoGravar}
                        grade={UMA_COLUNA}
                        mensagemDeSucesso="Nome salvo"
                      />
                    </div>
                    {perfis.map((p) => (
                      <Badge key={p.papel} className="mt-1.5 gap-1.5 rounded-md">
                        <Icones.Conferida />
                        {p.nome}
                      </Badge>
                    ))}
                  </div>
                  {perfis.map((p) => (
                    <p key={p.papel} className="m-0 text-sm text-muted-foreground first-letter:uppercase">
                      {perfis.length > 1 ? `${p.nome}: ${p.pode}` : p.pode}
                    </p>
                  ))}
                </div>
                <div className="h-px bg-(--borda)" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <CampoDoPerfil rotulo="CPF" Icone={Icones.Numero} valor={perfil.cpf === null ? null : <span className="tabular-nums">{perfil.cpf}</span>} />
                  <CampoDoPerfil rotulo="Cargo" Icone={Icones.Cargo} valor={perfil.cargo} />
                  <div className="sm:col-span-2">
                    <CampoDoPerfil rotulo={variasEntidades ? "Entidades" : "Entidade"} Icone={Icones.Orgao} valor={entidade} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            aria-labelledby="perfil-contato-titulo"
            className="flex min-w-0 flex-col gap-4 border-t border-(--borda) pt-5 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8"
          >
            <div className="flex flex-col gap-1">
              <h2 id="perfil-contato-titulo" className="m-0 text-base font-semibold text-foreground">
                Contato
              </h2>
              <p className="m-0 text-sm text-muted-foreground">Para onde vão os avisos por e-mail e por WhatsApp.</p>
            </div>
            <SecaoDeCampos
              titulo="Contato"
              tituloVisivel={false}
              prefixo="perfil-contato"
              campos={CAMPOS_DE_CONTATO}
              valores={valores}
              aoGravar={aoGravar}
              grade={UMA_COLUNA}
              mensagemDeSucesso="Contato salvo"
            />
            <div className="flex items-start gap-2 rounded-lg border border-(--borda) bg-muted/40 p-3">
              <span aria-hidden="true" className="mt-0.5 flex text-muted-foreground">
                <Icones.Cadeado tamanho={16} />
              </span>
              <p className="m-0 text-xs leading-relaxed text-muted-foreground">
                O CPF <span className="font-medium text-foreground tabular-nums">{perfil.cpf ?? NAO_INFORMADO}</span> é com ele que você
                entra no sistema e não se altera aqui. Para trocá-lo, fale com quem administra as contas da entidade.
              </p>
            </div>
          </section>
        </div>
      </CardContent>
    </Card>
  )
}

const ABAS_DAS_DESIGNACOES: DescricaoDeAba<"vigentes" | "encerradas">[] = [
  { chave: "vigentes", rotulo: "Vigentes", dica: "Mostra as designações em vigor" },
  { chave: "encerradas", rotulo: "Encerradas", dica: "Mostra as designações que já terminaram" },
]

function Designacoes({ perfil, aoAbrirContrato }: { perfil: PerfilDaConta; aoAbrirContrato: (id: number) => void }) {
  const [situacao, definirSituacao] = useState<"vigentes" | "encerradas">("vigentes")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(10)
  const filtradas = perfil.designacoes.filter((d) => d.vigente === (situacao === "vigentes"))
  const ultima = Math.max(1, Math.ceil(filtradas.length / porPagina))
  const paginaAtual = Math.min(pagina, ultima)
  const visiveis = filtradas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  return (
    <div className="flex flex-col gap-3 px-6 py-5">
      <h3 className="m-0 flex items-center gap-1.5 text-sm font-semibold text-(--tinta)">
        Contratos em que estou designado
        <ComDica dica="A designação é feita por portaria e não vem do perfil da conta. Sem designação você vê os contratos, mas não atesta medição nem registra ocorrência. Clique na linha para abrir a ficha." />
      </h3>
      <TablePrincipal2
        data={visiveis}
        pageCount={ultima}
        currentPage={paginaAtual}
        pageSize={porPagina}
        totalRegistros={filtradas.length}
        onPageChange={definirPagina}
        onPageSizeChange={(quantos) => {
          definirPorPagina(quantos)
          definirPagina(1)
        }}
        listrada
        aoClicarNaLinha={(designacao) => aoAbrirContrato(designacao.contrato_id)}
        abasNaBarra={
          <Abas
            nome="designacoes-do-perfil"
            rotulo="Situação das designações"
            classe="mb-0"
            abas={ABAS_DAS_DESIGNACOES}
            atual={situacao}
            aoTrocar={(chave) => {
              definirSituacao(chave)
              definirPagina(1)
            }}
          />
        }
        mensagemVazia={
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icones.Fiscalizacao />
              </EmptyMedia>
              <EmptyTitle>{situacao === "vigentes" ? "Nenhuma designação em vigor" : "Nenhuma designação encerrada"}</EmptyTitle>
              <EmptyDescription>
                {situacao === "vigentes" ? "Peça a designação ao setor responsável." : "As designações que terminarem aparecem aqui."}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        }
      >
        <Column<DesignacaoNoPerfil>
          name="numero"
          label="Contrato"
          enableSorting={false}
          render={(_, d) => <span className="font-medium whitespace-nowrap">{numeroComAno(d.numero, d.ano)}</span>}
        />
        <Column<DesignacaoNoPerfil>
          name="objeto"
          label="Objeto"
          enableSorting={false}
          render={(objeto) => <span className="block min-w-64 whitespace-normal">{String(objeto)}</span>}
        />
        <Column<DesignacaoNoPerfil> name="papel_rotulo" label="Meu papel" enableSorting={false} />
        <Column<DesignacaoNoPerfil>
          name="ato_de_designacao"
          label="Ato"
          enableSorting={false}
          render={(ato) => (ato === null ? <span className="text-(--tinta-3)">Não registrado</span> : String(ato))}
        />
        <Column<DesignacaoNoPerfil> name="inicio" label="Desde" enableSorting={false} render={(inicio) => data(String(inicio))} />
        <Column<DesignacaoNoPerfil>
          name="vigente"
          label="Situação"
          enableSorting={false}
          render={(_, d) => (
            <Selo situacao={d.vigente ? "vigente" : "encerrado"}>{d.vigente ? "Vigente" : `Encerrada em ${data(d.fim)}`}</Selo>
          )}
        />
      </TablePrincipal2>
    </div>
  )
}

function minimoDaPolitica(politica: string): number | null {
  const achado = /(\d+)\s*caracteres/i.exec(politica)
  return achado === null ? null : Number(achado[1])
}

function Regra({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li data-ok={ok ? "sim" : "nao"} className={cn("flex items-center gap-1.5", ok ? "text-(--status-sucesso)" : "text-destructive")}>
      {ok ? <Icones.Validada tamanho={14} /> : <Icones.Fechar tamanho={14} />}
      <span>{children}</span>
      <span className="sr-only">{ok ? "atendida" : "ainda não atendida"}</span>
    </li>
  )
}

export function TrocaDeSenha({
  perfil,
  aoTrocar,
  semSaida = false,
  forma = "em-linha",
}: {
  perfil: PerfilDaConta
  aoTrocar: (credencial: string, perfil: PerfilDaConta) => void
  semSaida?: boolean
  forma?: "em-linha" | "em-duas-linhas" | "na-entrada"
}) {
  const [atual, definirAtual] = useState("")
  const [nova, definirNova] = useState("")
  const [repetida, definirRepetida] = useState("")
  const [recusa, definirRecusa] = useState<string | null>(null)
  const [enviando, definirEnviando] = useState(false)

  const duasLinhas = forma === "em-duas-linhas"
  const naEntrada = forma === "na-entrada"
  const campo = duasLinhas || naEntrada ? "flex w-full flex-col" : "flex-[1_1_240px]"
  const linha = "grid grid-cols-1 gap-3 sm:grid-cols-2"
  const conferem = nova !== "" && nova === repetida
  const diferentes = nova !== "" && repetida !== "" && !conferem
  const minimo = minimoDaPolitica(perfil.politica_da_senha ?? "")

  const campoAtual = (
    <div className={campo}>
      <RotuloDoCampo htmlFor="senha-atual" obrigatorio>
        Senha atual
      </RotuloDoCampo>
      <PasswordInput
        id="senha-atual"
        required
        name="senha-atual"
        autoComplete="current-password"
        value={atual}
        onChange={(evento) => definirAtual(evento.target.value)}
        disabled={enviando}
        className="w-full"
      />
    </div>
  )
  const campoNova = (
    <div className={campo}>
      <RotuloDoCampo htmlFor="senha-nova" obrigatorio>
        Senha nova
      </RotuloDoCampo>
      <PasswordInput
        id="senha-nova"
        required
        name="senha-nova"
        autoComplete="new-password"
        value={nova}
        onChange={(evento) => definirNova(evento.target.value)}
        disabled={enviando}
        className="w-full"
      />
    </div>
  )
  const campoRepetida = (
    <div className={campo}>
      <RotuloDoCampo htmlFor="senha-repetida" obrigatorio>
        Repita a senha nova
      </RotuloDoCampo>
      <PasswordInput
        id="senha-repetida"
        required
        name="senha-repetida"
        autoComplete="new-password"
        value={repetida}
        aria-invalid={diferentes || undefined}
        aria-describedby={diferentes ? "senha-repetida-recusa" : undefined}
        onChange={(evento) => definirRepetida(evento.target.value)}
        disabled={enviando}
      />
      {diferentes && <InputError id="senha-repetida-recusa" message="A nova senha não confere." />}
    </div>
  )
  const podeEnviar = atual !== "" && conferem && !enviando
  const falta =
    faltaNoFormulario([
      ["a senha atual", atual],
      ["a senha nova", nova],
      ["a senha nova repetida", repetida],
    ]) ?? (conferem ? null : "As duas senhas novas não conferem")

  const enviar = async () => {
    definirEnviando(true)
    definirRecusa(null)
    try {
      const resposta = await api.trocarMinhaSenha(atual, nova)
      definirAtual("")
      definirNova("")
      definirRepetida("")
      toast.success("Senha trocada", { description: "As sessões abertas com a senha anterior foram encerradas." })
      aoTrocar(resposta.credencial, resposta.perfil)
    } catch (falha: unknown) {
      const mensagem = mensagemDeRecusa(falha)
      definirRecusa(mensagem)
      toast.error(mensagem)
    } finally {
      definirEnviando(false)
    }
  }

  const respiro = duasLinhas ? "px-4" : undefined
  const politica = (
    <div data-slot="politica-da-senha" className="flex flex-col gap-1">
      <p className={AJUDA}>O que esta entidade pede: {perfil.politica_da_senha}</p>
      {(nova !== "" || repetida !== "") && (
        <ul className="m-0 flex list-none flex-row flex-wrap gap-x-4 gap-y-0.5 p-0 text-sm" aria-live="polite">
          {minimo !== null && <Regra ok={nova.length >= minimo}>{`${inteiro(minimo)} caracteres ou mais`}</Regra>}
          <Regra ok={conferem}>As duas senhas novas são iguais</Regra>
        </ul>
      )}
    </div>
  )

  const formulario = (
    <div
      data-slot="formulario-da-senha"
      data-forma={forma}
      className={
        naEntrada ? "flex flex-col gap-3" : duasLinhas ? "mt-3 flex flex-col gap-3" : "mt-3 rounded-md border border-(--borda-forte) bg-card p-3.5"
      }
    >
      {naEntrada ? (
        <>
          {campoAtual}
          <div className="flex flex-col gap-1.5">
            {campoNova}
            {politica}
          </div>
          {campoRepetida}
        </>
      ) : duasLinhas ? (
        <>
          <div data-slot="linha-do-formulario" className={linha}>
            {campoAtual}
          </div>
          {politica}
          <div data-slot="linha-do-formulario" className={linha}>
            {campoNova}
            {campoRepetida}
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-3">
          {politica}
          <div className="flex flex-wrap items-start gap-3">
            {campoAtual}
            {campoNova}
            {campoRepetida}
          </div>
        </div>
      )}

      {recusa !== null && (
        <p role="alert" className={RECUSA}>
          {recusa}
        </p>
      )}

      <div className={duasLinhas || naEntrada ? "flex flex-wrap justify-end gap-2" : "mt-3.5 flex flex-wrap justify-end gap-2"}>
        <Button size="sm" title={falta ?? undefined} type="button" disabled={!podeEnviar} onClick={() => void enviar()}>
          <Icones.Chave />
          {enviando ? "Trocando..." : "Trocar a senha"}
        </Button>
      </div>
    </div>
  )

  if (naEntrada) return formulario

  return (
    <Card className={semSaida ? DESTAQUE : undefined}>
      <CardHeader className={respiro}>
        <CardTitle>
          Minha senha{" "}
          <ComDica dica="Ao trocar a senha, as sessões abertas com a senha anterior, em outros computadores ou navegadores, são encerradas." />
        </CardTitle>
        <CardAction>
          {perfil.senha_provisoria ? (
            <Selo situacao="senha_provisoria">Senha provisória</Selo>
          ) : perfil.senha_alterada_em === null ? (
            <Badge variant="default" title="A senha desta conta nunca foi trocada desde que foi criada">
              <Icones.Informacao />
              Nunca trocada
            </Badge>
          ) : (
            <Selo situacao="senha_do_dono">{`Trocada por você em ${dataHora(perfil.senha_alterada_em)}`}</Selo>
          )}
        </CardAction>
      </CardHeader>

      <CardContent className={respiro}>
        {perfil.senha_provisoria && (
          <p className={FAZER}>
            <strong>Esta senha foi escolhida por outra pessoa.</strong> Troque-a para que os atos desta conta identifiquem só você.
          </p>
        )}

        {formulario}
      </CardContent>
    </Card>
  )
}

function MinhasEntradas() {
  const [entradas, definirEntradas] = useState<RespostaDasEntradas | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .minhasEntradas(LIMITE_DE_ENTRADAS)
      .then(definirEntradas)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const quantas =
    entradas === null
      ? ""
      : entradas.total > entradas.dados.length
        ? `As ${inteiro(entradas.dados.length)} mais recentes de ${inteiro(entradas.total)}`
        : `${inteiro(entradas.total)} ${entradas.total === 1 ? "entrada" : "entradas"}`

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Minhas últimas entradas{" "}
          <ComDica dica="As entradas mais recentes nesta conta, com as recusadas. Se não foi você, troque a senha." />
        </CardTitle>
        {entradas !== null && (
          <CardAction>
            <span className={CONTA}>{quantas}</span>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {entradas === null ? (
          falha !== null ? (
            <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
          ) : (
            <CarregandoNaSecao />
          )
        ) : (
          <>
            {entradas.recusadas > 0 && (
              <p className="m-0 flex items-center gap-1.5 text-sm text-destructive">
                <Icones.Risco tamanho={16} />
                {`${inteiro(entradas.recusadas)} ${entradas.recusadas === 1 ? "entrada recusada" : "entradas recusadas"}. Se não foi você, troque a senha.`}
              </p>
            )}
            <TablePrincipal2
              data={entradas.dados}
              paginacao={false}
              pageCount={1}
              currentPage={1}
              onPageChange={() => undefined}
              listrada
              mensagemVazia="Nenhuma entrada registrada nesta conta."
            >
              <Column<EntradaNoPerfil>
                name="momento"
                label="Quando"
                enableSorting={false}
                render={(momento) => <span className="whitespace-nowrap tabular-nums">{dataHora(String(momento))}</span>}
              />
              <Column<EntradaNoPerfil>
                name="recusada"
                label="Resultado"
                enableSorting={false}
                render={(_, entrada) => (
                  <Selo situacao={entrada.recusada ? "recusada" : entrada.com_senha_provisoria ? "senha_provisoria" : "concluida"}>
                    {entrada.recusada
                      ? `Recusada${entrada.motivo ? ` · ${entrada.motivo}` : ""}`
                      : entrada.com_senha_provisoria
                        ? "Entrou com senha provisória"
                        : "Entrou"}
                  </Selo>
                )}
              />
              <Column<EntradaNoPerfil>
                name="endereco_de_rede"
                label="Endereço de rede"
                enableSorting={false}
                render={(endereco) => (endereco === null ? <span className="text-(--tinta-3)">{NAO_INFORMADO}</span> : String(endereco))}
              />
            </TablePrincipal2>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function Atos({ perfil }: { perfil: PerfilDaConta }) {
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(10)
  const [aberto, definirAberto] = useState<AtoNoPerfil | null>(null)
  const total = perfil.atos.length
  const ultima = Math.max(1, Math.ceil(total / porPagina))
  const paginaAtual = Math.min(pagina, ultima)
  const visiveis = perfil.atos.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)
  const recorte =
    perfil.atos_totais > total
      ? `Mostra os ${inteiro(total)} mais recentes de ${inteiro(perfil.atos_totais)}.`
      : `Todos os seus ${inteiro(perfil.atos_totais)} atos.`

  return (
    <div className="flex flex-col gap-3 px-6 py-5">
      <h3 className="m-0 flex items-center gap-1.5 text-sm font-semibold text-(--tinta)">
        Minha atividade
        <ComDica dica={`O que você fez no sistema. Consultar não conta como ato. ${recorte} Clique na linha para ver o registro inteiro.`} />
      </h3>
      <TablePrincipal2
        data={visiveis}
        pageCount={ultima}
        currentPage={paginaAtual}
        pageSize={porPagina}
        totalRegistros={total}
        onPageChange={definirPagina}
        onPageSizeChange={(quantidade) => {
          definirPorPagina(quantidade)
          definirPagina(1)
        }}
        listrada
        aoClicarNaLinha={definirAberto}
        mensagemVazia={
          <Empty className="py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icones.Historico />
              </EmptyMedia>
              <EmptyTitle>Nenhum ato ainda</EmptyTitle>
              <EmptyDescription>Você ainda não praticou nenhum ato neste sistema. Consultar não conta como ato.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        }
      >
        <Column<AtoNoPerfil>
          name="momento"
          label="Quando"
          enableSorting={false}
          render={(momento) => <span className="whitespace-nowrap tabular-nums">{dataHora(String(momento))}</span>}
        />
        <Column<AtoNoPerfil> name="ato" label="Ato" enableSorting={false} render={(ato) => <span className={MAIUSCULA_INLINE}>{String(ato)}</span>} />
        <Column<AtoNoPerfil>
          name="operacao_rotulo"
          label="O quê"
          enableSorting={false}
          render={(_, ato) => (
            <span className={MAIUSCULA_INLINE}>{ato.campo_rotulo === null ? ato.operacao_rotulo : `${ato.operacao_rotulo} · ${ato.campo_rotulo}`}</span>
          )}
        />
        <Column<AtoNoPerfil> name="valor_novo" label="Valor" enableSorting={false} render={(valor) => resumo(valor as string | null)} />
      </TablePrincipal2>

      <Sheet open={aberto !== null} onOpenChange={(abrir) => !abrir && definirAberto(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-xl [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">Meu ato</SheetTitle>
          </SheetHeader>
          {aberto !== null && (
            <div className="flex flex-col gap-4 px-6 pt-3 pb-6">
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                {(
                  [
                    ["Quando", dataHora(aberto.momento)],
                    ["Ato", aberto.ato],
                    ["O quê", aberto.campo_rotulo === null ? aberto.operacao_rotulo : `${aberto.operacao_rotulo} · ${aberto.campo_rotulo}`],
                  ] as [string, string][]
                ).map(([rotulo, valor]) => (
                  <div key={rotulo} className="contents">
                    <dt className="text-(--tinta-2)">{rotulo}</dt>
                    <dd className={cn("m-0 text-(--tinta)", MAIUSCULA_INLINE)}>{valor}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold tracking-wide text-(--tinta-2) uppercase">Valor</span>
                <p className="m-0 rounded-md border border-(--borda) bg-muted p-3 text-sm whitespace-pre-wrap text-(--tinta) wrap-anywhere">
                  {aberto.valor_novo ?? "—"}
                </p>
              </div>
              <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
                <Button type="button" variant="outline" onClick={() => definirAberto(null)}>
                  <Icones.Fechar />
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
